import {
  type ReactNode,
  createContext,
  createElement,
  useContext,
  useEffect,
  useState,
} from 'react';
import i18next from 'i18next';
import { type TransProps, initReactI18next, Trans } from 'react-i18next';
import { capitalize } from 'lodash-es';
import { IS_PROD_ENV } from '#/lib/constants';
import enTranslations from './translations/en.json';

const translationsByLanguage = {
  en: enTranslations,
  // add more languages as necessary
} as const;

const language = window.navigator.language;

interface State {
  locale: string;
}

// see https://www.grammarly.com/blog/sentences/title-case-sentence-case/
type TranslationCasing = 'sentence-case' | 'lower-case';

interface Locale extends State {
  setLocale: (lang: string) => Promise<void>;
  translate: (key: string, options?: any, casing?: TranslationCasing) => string;
  translateBold: (key: string, values?: Record<string, string>) => ReactNode;
  translateComplex: ({
    key,
    values,
    components,
  }: {
    key: string;
    values?: Record<string, string>;
    components?: TransProps<string>['components'];
  }) => ReactNode;
}

const boldWrapper = createElement('strong');

const defaultState: State = {
  locale: language,
};
const defaultLocale: Locale = {
  ...defaultState,
  setLocale: () => Promise.resolve(),
  translate: (s) => s,
  translateBold: (s) => s,
  translateComplex: ({ key }) => key,
};

const contextInstance = i18next.createInstance();
const WebComponentLocaleContext = createContext(defaultLocale);
export const useLocale = (): Locale => useContext(WebComponentLocaleContext);

const changeLocale = async (locale: string) => {
  const language = locale.split('-')[0]; // e.g. 'en'
  const translations = translationsByLanguage[language] ?? enTranslations;
  contextInstance.addResources(locale, 'translation', translations);
  contextInstance.changeLanguage(locale);
};

export interface WebComponentLocaleProvideProps {
  children: ReactNode;
}
export const WebComponentLocaleProvider = ({
  children,
}: WebComponentLocaleProvideProps) => {
  const [initialized, setInitialized] = useState(false);
  const [state, setState] = useState(defaultState);
  const locale = state.locale; // en-US

  // This must be called by the app to set language
  const setLocale = async (locale: string) => {
    if (!locale) {
      throw new Error('setLocale called with undefined value');
    }
    if (locale === state.locale) {
      return;
    }
    await changeLocale(locale);
    setState({
      ...state,
      locale,
    });
  };

  useEffect(() => {
    contextInstance
      .use(initReactI18next)
      .init({
        lng: 'en',
        fallbackLng: 'en',
        debug: !IS_PROD_ENV,
        keySeparator: false,
        resources: {
          en: {
            translation: enTranslations,
          },
        },
      })
      .then(() => setInitialized(true))
      .catch((e) => {
        throw new Error(`Failed to init LocaleProvider: ${e}`);
      });
  }, [initialized]);

  useEffect(() => {
    setLocale(locale);
    // eslint-disable-next-line
  }, []);

  const translate = (
    key: string,
    options?: Record<string, string>,
    casing?: TranslationCasing
  ) => {
    const t = contextInstance.t(key, options);

    if (casing === 'sentence-case') {
      return capitalize(t);
    }

    if (casing === 'lower-case') {
      return t.toLocaleLowerCase();
    }

    return t;
  };

  const translateBold = (key: string, values?: Record<string, string>) =>
    createElement(Trans, {
      components: { bold: boldWrapper },
      i18nKey: key,
      values,
    });

  const translateComplex = ({
    key,
    values,
    components,
  }: {
    key: string;
    values?: Record<string, string>;
    components?: TransProps<string>['components'];
  }) => <Trans components={components} i18nKey={key} values={values}></Trans>;

  return (
    <WebComponentLocaleContext.Provider
      value={{
        locale,
        setLocale,
        translate,
        translateBold,
        translateComplex,
      }}
    >
      {children}
    </WebComponentLocaleContext.Provider>
  );
};
